import {
    type AnatomyMeasurement,
    type AnatomyPart,
    type AnatomyRect,
    isCircle
} from "./measure";
import {
    findSizeTokens,
    isFullRadius
} from "./tokens";

export type AnatomySide = "bottom" | "right" | "left" | "top";

export type AnatomySegmentKind = "overall" | "border" | "travel" | "inset" | "part" | "gap";

export type AnatomyPoint = {
    x: number;
    y: number;
};

export type AnatomyLabel = {
    center: number;
    prefix: string;
    token: string;
    value: string;
    width: number;
    row: number;
};

export type AnatomySegment = {
    kind: AnatomySegmentKind;
    partIds: Array<number>;
    label: AnatomyLabel;
    start: number;
    end: number;
    key: string;
};

export type AnatomyGuide = {
    isCenter: boolean;
    position: number;
    from: number;
    to: number;
};

export type AnatomyChain = {
    segments: Array<AnatomySegment>;
    guides: Array<AnatomyGuide>;
    boundaries: Array<number>;
    crossStart: number;
    rowHeight: number;
    side: AnatomySide;
    crossEnd: number;
    edge: number;
    line: number;
};

export type AnatomyGhost = {
    stateIndex: number;
    rect: AnatomyRect;
    partId: number;
};

export type AnatomyBalloonTarget = {
    partId: number | null;
    number: number;
};

export type AnatomyBalloon = AnatomyBalloonTarget & {
    balloon: AnatomyPoint;
    anchor: AnatomyPoint;
};

export type AnatomyLayout = {
    balloons: Array<AnatomyBalloon>;
    chains: Array<AnatomyChain>;
    stageHeight: number;
    stageWidth: number;
    gridStep: number;
    originX: number;
    originY: number;
    scale: number;
};

export type AnatomyLayoutOptions = {
    balloonTargets: Array<AnatomyBalloonTarget>;
    measurement: AnatomyMeasurement;
    ghosts: Array<AnatomyGhost>;
    availableWidth: number;
    travelPrefix: string;
    maxHeight: number;
    maxScale: number;
};

type AnatomyInterval = {
    partIds: Array<number>;
    start: number;
    end: number;
};

type AnatomyChainSpec = {
    intervals: Array<AnatomyInterval>;
    container: AnatomyRect;
    borderStart: number;
    isOverall: boolean;
    borderEnd: number;
    isDetail: boolean;
    side: AnatomySide;
};

type AnatomyRawSegment = {
    kind: AnatomySegmentKind;
    partIds: Array<number>;
    start: number;
    end: number;
};

type AnatomyRawGuide = {
    isCenter: boolean;
    position: number;
    from: number;
};

type AnatomyRawChain = {
    segments: Array<AnatomyRawSegment>;
    guides: Array<AnatomyRawGuide>;
    container: AnatomyRect;
    isDetail: boolean;
    side: AnatomySide;
    order: number;
};

type AnatomySidedChain = {
    segments: Array<AnatomySegment>;
    raw: AnatomyRawChain;
    rowHeight: number;
    rows: number;
};

const SCALES = [
    5,
    4,
    3,
    2.5,
    2,
    1.5,
    1,
    0.75,
    0.5
];

const MAX_GRID_STEP = 64;

const GRID_STEPS = [
    4,
    8,
    16,
    32,
    MAX_GRID_STEP
];

const SCALE_BAR_LENGTHS = [
    4,
    8,
    10,
    16,
    20,
    32,
    40,
    64,
    80,
    160
];

const CONTROL_SIZE_PREFIXES = [
    "controlHeights",
    "touchTargets"
];

const AVATAR_SIZE_PREFIXES = [
    "avatarSizes"
];

const ICON_SIZE_PREFIXES = [
    "iconSizes"
];

export const RULER_SIZE = 22;

export const LABEL_GAP = 6;

export const LINE_HEIGHT = 12;

const STAGE_PADDING = 28;

const TIER_GAP = 18;

const LABEL_SPACING = 12;

const VALUE_CHAR_WIDTH = 6.9;

const TOKEN_CHAR_WIDTH = 5.8;

const MIN_DETAIL_EXTENT = 120;

const MAX_DETAIL_CONTAINERS = 2;

const MIN_GRID_PIXELS = 12;

const MIN_SCALE_BAR_PIXELS = 96;

const BALLOON_SIZE = 18;

const BALLOON_OFFSET = 9;

const TIER_ORDER = {
    overall: 3,
    detail: 1,
    travel: 2,
    focus: 0
};

const isHorizontalSide = (side: AnatomySide) => side === "top" || side === "bottom";

export const formatValue = (value: number) => String(Math.round(value * 10) / 10);

const getChildren = (parts: Array<AnatomyPart>, parentId: number | null) => parts.filter((part) => part.parentId === parentId);

const getArea = (rect: AnatomyRect) => rect.width * rect.height;

const hasPrefix = (name: string, prefixes: Array<string>) => prefixes.some((prefix) => name.startsWith(`${prefix}.`));

export const getSizePrefixes = (part: AnatomyPart) => {
    switch(part.kind) {
        case "icon":
            return ICON_SIZE_PREFIXES;
        case "media":
            return AVATAR_SIZE_PREFIXES;
        case "text":
            return [];
        default:
            return isCircle(part) ? [
                ...CONTROL_SIZE_PREFIXES,
                ...AVATAR_SIZE_PREFIXES
            ] : CONTROL_SIZE_PREFIXES;
    }
};

const toIntervals = (parts: Array<AnatomyPart>, side: AnatomySide): Array<AnatomyInterval> => parts.map((part) => isHorizontalSide(side) ? {
    end: part.rect.x + part.rect.width,
    start: part.rect.x,
    partIds: [part.id]
} : {
    end: part.rect.y + part.rect.height,
    start: part.rect.y,
    partIds: [part.id]
});

const getBorderRange = (part: AnatomyPart | undefined, side: AnatomySide) => {
    if(!part) {
        return {
            borderStart: 0,
            borderEnd: 0
        };
    }

    return isHorizontalSide(side) ? {
        borderStart: part.borderInsets.left,
        borderEnd: part.borderInsets.right
    } : {
        borderEnd: part.borderInsets.bottom,
        borderStart: part.borderInsets.top
    };
};

const getEdge = (rect: AnatomyRect, side: AnatomySide) => {
    switch(side) {
        case "top":
            return rect.y;
        case "bottom":
            return rect.y + rect.height;
        case "left":
            return rect.x;
        default:
            return rect.x + rect.width;
    }
};

const isFartherOut = (candidate: number, current: number, side: AnatomySide) => side === "top" || side === "left" ? candidate < current : candidate > current;

const getRootRect = (measurement: AnatomyMeasurement): AnatomyRect => ({
    height: measurement.height,
    width: measurement.width,
    x: 0,
    y: 0
});

const getChainSpecs = (measurement: AnatomyMeasurement): Array<AnatomyChainSpec> => {
    const {
        height,
        width,
        parts
    } = measurement;

    const root = getRootRect(measurement);

    let container = root;

    let containerPart: AnatomyPart | undefined;

    let level = getChildren(parts, null);

    const ancestorIds: Array<number> = [];

    let only = level.length === 1 ? level[0] : undefined;

    while(only && only.childIds.length > 0 && only.rect.width >= width - 1 && only.rect.height >= height - 1) {
        container = only.rect;

        containerPart = only;

        ancestorIds.push(only.id);

        level = getChildren(parts, only.id);

        only = level.length === 1 ? level[0] : undefined;
    }

    const specs: Array<AnatomyChainSpec> = [
        {
            ...getBorderRange(containerPart, "top"),
            intervals: toIntervals(level, "top"),
            isOverall: false,
            isDetail: false,
            side: "top",
            container
        },
        {
            ...getBorderRange(containerPart, "left"),
            intervals: toIntervals(level, "left"),
            isOverall: false,
            isDetail: false,
            side: "left",
            container
        }
    ];

    const detailContainers = parts.filter((part) => part.kind === "shape" && part.childIds.length > 0 && !ancestorIds.includes(part.id)).sort((first, second) => first.depth - second.depth || getArea(second.rect) - getArea(first.rect)).slice(0, MAX_DETAIL_CONTAINERS);

    detailContainers.forEach((detail) => {
        const children = getChildren(parts, detail.id);

        specs.push({
            ...getBorderRange(detail, "bottom"),
            intervals: toIntervals(children, "bottom"),
            container: detail.rect,
            isOverall: false,
            isDetail: true,
            side: "bottom"
        }, {
            ...getBorderRange(detail, "left"),
            intervals: toIntervals(children, "left"),
            container: detail.rect,
            isOverall: false,
            isDetail: true,
            side: "left"
        });
    });

    specs.push({
        container: root,
        isDetail: false,
        isOverall: true,
        borderStart: 0,
        intervals: [],
        borderEnd: 0,
        side: "top"
    }, {
        container: root,
        isDetail: false,
        isOverall: true,
        borderStart: 0,
        intervals: [],
        side: "left",
        borderEnd: 0
    });

    return specs;
};

const buildSegments = (spec: AnatomyChainSpec): Array<AnatomyRawSegment> => {
    const isHorizontal = isHorizontalSide(spec.side);

    const start = isHorizontal ? spec.container.x : spec.container.y;

    const end = start + (isHorizontal ? spec.container.width : spec.container.height);

    if(spec.isOverall) {
        return [
            {
                kind: "overall",
                partIds: [],
                start,
                end
            }
        ];
    }

    const merged: Array<AnatomyInterval> = [];

    [...spec.intervals].sort((first, second) => first.start - second.start || second.end - first.end).forEach((interval) => {
        const last = merged[merged.length - 1];

        if(last && interval.start < last.end - 0.5) {
            last.end = Math.max(last.end, interval.end);

            last.partIds = [
                ...last.partIds,
                ...interval.partIds
            ];

            return;
        }

        merged.push({
            ...interval,
            partIds: [...interval.partIds]
        });
    });

    const segments: Array<AnatomyRawSegment> = [];

    const innerEnd = end - spec.borderEnd;

    let cursor = start + spec.borderStart;

    let previousIds: Array<number> = [];

    if(spec.borderStart >= 0.5) {
        segments.push({
            kind: "border",
            end: cursor,
            partIds: [],
            start
        });
    }

    merged.forEach((interval) => {
        const intervalStart = Math.max(interval.start, cursor);

        const intervalEnd = Math.min(interval.end, innerEnd);

        if(intervalEnd - intervalStart < 0.5) {
            return;
        }

        if(intervalStart - cursor >= 0.5) {
            segments.push({
                kind: previousIds.length === 0 ? "inset" : "gap",
                partIds: [
                    ...previousIds,
                    ...interval.partIds
                ],
                end: intervalStart,
                start: cursor
            });
        }

        segments.push({
            partIds: interval.partIds,
            start: intervalStart,
            end: intervalEnd,
            kind: "part"
        });

        cursor = intervalEnd;

        previousIds = interval.partIds;
    });

    if(previousIds.length === 0) {
        return [];
    }

    if(innerEnd - cursor >= 0.5) {
        segments.push({
            partIds: previousIds,
            end: innerEnd,
            kind: "inset",
            start: cursor
        });
    }

    if(spec.borderEnd >= 0.5) {
        segments.push({
            start: innerEnd,
            kind: "border",
            partIds: [],
            end
        });
    }

    return segments;
};

const buildGuides = (spec: AnatomyChainSpec, segments: Array<AnatomyRawSegment>, measurement: AnatomyMeasurement): Array<AnatomyRawGuide> => {
    const isHorizontal = isHorizontalSide(spec.side);

    const rootEdge = getEdge(getRootRect(measurement), spec.side);

    const containerStart = isHorizontal ? spec.container.x : spec.container.y;

    const containerEnd = containerStart + (isHorizontal ? spec.container.width : spec.container.height);

    const boundaries = Array.from(new Set(segments.flatMap((segment) => [
        segment.start,
        segment.end
    ])));

    const guides: Array<AnatomyRawGuide> = [];

    boundaries.forEach((boundary) => {
        let objectEdge: number | null = null;

        if(Math.abs(boundary - containerStart) < 0.5 || Math.abs(boundary - containerEnd) < 0.5) {
            objectEdge = getEdge(spec.container, spec.side);
        }

        spec.intervals.forEach((interval) => {
            if(Math.abs(interval.start - boundary) >= 0.5 && Math.abs(interval.end - boundary) >= 0.5) {
                return;
            }

            interval.partIds.forEach((partId) => {
                const part = measurement.parts[partId];

                if(!part) {
                    return;
                }

                const edge = getEdge(part.rect, spec.side);

                if(objectEdge === null || isFartherOut(edge, objectEdge, spec.side)) {
                    objectEdge = edge;
                }
            });
        });

        if(objectEdge !== null && Math.abs(objectEdge - rootEdge) >= 0.5) {
            guides.push({
                position: boundary,
                from: objectEdge,
                isCenter: false
            });
        }
    });

    return guides;
};

const buildTravelChains = (measurement: AnatomyMeasurement, ghosts: Array<AnatomyGhost>): Array<AnatomyRawChain> => ghosts.flatMap((ghost) => {
    const part = measurement.parts[ghost.partId];

    if(!part) {
        return [];
    }

    const solidX = part.rect.x + part.rect.width / 2;

    const solidY = part.rect.y + part.rect.height / 2;

    const ghostX = ghost.rect.x + ghost.rect.width / 2;

    const ghostY = ghost.rect.y + ghost.rect.height / 2;

    const isHorizontal = Math.abs(ghostX - solidX) >= Math.abs(ghostY - solidY);

    const solidAlong = isHorizontal ? solidX : solidY;

    const ghostAlong = isHorizontal ? ghostX : ghostY;

    if(Math.abs(ghostAlong - solidAlong) < 0.5) {
        return [];
    }

    return [
        {
            segments: [
                {
                    start: Math.min(solidAlong, ghostAlong),
                    end: Math.max(solidAlong, ghostAlong),
                    partIds: [part.id],
                    kind: "travel"
                }
            ],
            guides: [
                {
                    from: isHorizontal ? solidY : solidX,
                    position: solidAlong,
                    isCenter: true
                },
                {
                    from: isHorizontal ? ghostY : ghostX,
                    position: ghostAlong,
                    isCenter: true
                }
            ],
            side: isHorizontal ? "bottom" : "left",
            container: getRootRect(measurement),
            order: TIER_ORDER.travel,
            isDetail: false
        }
    ];
});

const resolveChains = (measurement: AnatomyMeasurement, ghosts: Array<AnatomyGhost>): Array<AnatomyRawChain> => {
    const chains: Array<AnatomyRawChain> = getChainSpecs(measurement).flatMap((spec) => {
        const segments = buildSegments(spec);

        if(!spec.isOverall && segments.length < 2) {
            return [];
        }

        let order = TIER_ORDER.focus;

        if(spec.isOverall) {
            order = TIER_ORDER.overall;
        } else if(spec.isDetail) {
            order = TIER_ORDER.detail;
        }

        return [
            {
                guides: buildGuides(spec, segments, measurement),
                container: spec.container,
                isDetail: spec.isDetail,
                side: spec.side,
                segments,
                order
            }
        ];
    });

    return [
        ...chains,
        ...buildTravelChains(measurement, ghosts)
    ];
};

const getSegmentTokens = (segment: AnatomyRawSegment, parts: Array<AnatomyPart>) => {
    const value = segment.end - segment.start;

    switch(segment.kind) {
        case "gap":
        case "inset":
            return findSizeTokens("space", value);
        case "overall":
            return findSizeTokens("size", value).filter((name) => hasPrefix(name, CONTROL_SIZE_PREFIXES));
        case "border":
        case "travel":
            return [];
        default: {
            const partId = segment.partIds.length === 1 ? segment.partIds[0] : undefined;

            const part = partId === undefined ? undefined : parts[partId];

            return part ? findSizeTokens("size", value).filter((name) => hasPrefix(name, getSizePrefixes(part))) : [];
        }
    }
};

const getLabelWidth = (prefix: string, value: string, token: string) => Math.max((prefix ? prefix.length + 1 : 0) * VALUE_CHAR_WIDTH + value.length * VALUE_CHAR_WIDTH, token.length * TOKEN_CHAR_WIDTH);

const assignRows = (segments: Array<AnatomySegment>) => {
    const rowEnds: Array<number> = [];

    [...segments].sort((first, second) => first.label.center - second.label.center).forEach((segment) => {
        const labelStart = segment.label.center - segment.label.width / 2;

        let row = rowEnds.findIndex((rowEnd) => labelStart >= rowEnd + LABEL_SPACING);

        if(row === -1) {
            row = rowEnds.length;

            rowEnds.push(0);
        }

        rowEnds[row] = segment.label.center + segment.label.width / 2;

        segment.label.row = row;
    });

    return Math.max(1, rowEnds.length);
};

const getGridStep = (scale: number) => GRID_STEPS.find((step) => step * scale >= MIN_GRID_PIXELS) ?? MAX_GRID_STEP;

const getCalloutAnchor = (partId: number | null, measurement: AnatomyMeasurement): AnatomyPoint | null => {
    if(partId === null) {
        const hit = measurement.hitRect;

        return hit ? {
            y: hit.y + Math.min(hit.height / 4, 6),
            x: hit.x + hit.width
        } : null;
    }

    const part = measurement.parts[partId];

    if(!part) {
        return null;
    }

    const {
        height,
        width,
        x,
        y
    } = part.rect;

    if(part.kind === "text") {
        return {
            y: y + height / 2,
            x: x + width
        };
    }

    if(isCircle(part)) {
        const radius = width / 2;

        return {
            x: x + radius + radius * Math.SQRT1_2,
            y: y + radius - radius * Math.SQRT1_2
        };
    }

    const radius = isFullRadius(part.radius, width, height) ? Math.min(width, height) / 2 : Math.min(part.radius, width / 2, height / 2);

    return {
        x: x + width - radius,
        y
    };
};

const layoutForScale = (options: AnatomyLayoutOptions, rawChains: Array<AnatomyRawChain>, scale: number): AnatomyLayout => {
    const {
        availableWidth,
        balloonTargets,
        travelPrefix,
        measurement
    } = options;

    const {
        height,
        width,
        parts
    } = measurement;

    const sideChains: Record<AnatomySide, Array<AnatomySidedChain>> = {
        bottom: [],
        right: [],
        left: [],
        top: []
    };

    rawChains.forEach((raw, chainIndex) => {
        if(raw.isDetail && (isHorizontalSide(raw.side) ? raw.container.width : raw.container.height) * scale < MIN_DETAIL_EXTENT) {
            return;
        }

        const segments = raw.segments.map((segment, segmentIndex) => {
            const value = segment.kind === "border" ? "" : `${formatValue(segment.end - segment.start)}PX`;

            const prefix = segment.kind === "travel" ? travelPrefix : "";

            const token = getSegmentTokens(segment, parts)[0] ?? "";

            return {
                ...segment,
                label: {
                    center: (segment.start + segment.end) / 2 * scale,
                    width: getLabelWidth(prefix, value, token),
                    row: 0,
                    prefix,
                    token,
                    value
                },
                key: `${chainIndex}-${segmentIndex}`
            };
        });

        const hasToken = segments.some((segment) => segment.label.token.length > 0);

        sideChains[raw.side].push({
            rowHeight: hasToken ? LINE_HEIGHT * 2 + 2 : LINE_HEIGHT + 2,
            rows: assignRows(segments),
            segments,
            raw
        });
    });

    (Object.keys(sideChains) as Array<AnatomySide>).forEach((side) => {
        sideChains[side].sort((first, second) => first.raw.order - second.raw.order);
    });

    const getThickness = (side: AnatomySide) => sideChains[side].reduce((total, chain) => total + TIER_GAP + LABEL_GAP + chain.rows * chain.rowHeight, 0);

    const leftMargin = RULER_SIZE + STAGE_PADDING + getThickness("left");

    const labelOverflow = [
        ...sideChains.top,
        ...sideChains.bottom
    ].reduce((widest, chain) => chain.segments.reduce((current, segment) => Math.max(current, segment.label.center + segment.label.width / 2 - width * scale), widest), 0);

    const rightMargin = STAGE_PADDING + getThickness("right") + labelOverflow;

    const topMargin = RULER_SIZE + STAGE_PADDING + getThickness("top");

    const bottomMargin = STAGE_PADDING + getThickness("bottom");

    const objectWidth = width * scale;

    const originX = leftMargin + Math.max(0, (availableWidth - leftMargin - rightMargin - objectWidth) / 2);

    const originY = topMargin;

    const rootEdges: Record<AnatomySide, number> = {
        bottom: originY + height * scale,
        right: originX + objectWidth,
        left: originX,
        top: originY
    };

    const chains: Array<AnatomyChain> = [];

    (Object.keys(sideChains) as Array<AnatomySide>).forEach((side) => {
        const direction = side === "top" || side === "left" ? -1 : 1;

        const isHorizontal = isHorizontalSide(side);

        const axisOrigin = isHorizontal ? originX : originY;

        const crossOrigin = isHorizontal ? originY : originX;

        let outer = rootEdges[side];

        sideChains[side].forEach(({
            rowHeight,
            segments,
            rows,
            raw
        }) => {
            const line = outer + direction * TIER_GAP;

            outer = line + direction * (LABEL_GAP + rows * rowHeight);

            const boundaries = Array.from(new Set(segments.flatMap((segment) => [
                segment.start,
                segment.end
            ]))).sort((first, second) => first - second);

            chains.push({
                crossEnd: crossOrigin + getEdge(raw.container, isHorizontal ? "bottom" : "right") * scale,
                crossStart: crossOrigin + getEdge(raw.container, isHorizontal ? "top" : "left") * scale,
                boundaries: boundaries.map((boundary) => axisOrigin + boundary * scale),
                guides: raw.guides.map((guide) => ({
                    position: axisOrigin + guide.position * scale,
                    from: crossOrigin + guide.from * scale,
                    isCenter: guide.isCenter,
                    to: rootEdges[side]
                })),
                segments: segments.map((segment) => ({
                    ...segment,
                    label: {
                        ...segment.label,
                        center: axisOrigin + segment.label.center
                    },
                    start: axisOrigin + segment.start * scale,
                    end: axisOrigin + segment.end * scale
                })),
                edge: rootEdges[side],
                rowHeight,
                line,
                side
            });
        });
    });

    const placedBalloons: Array<AnatomyPoint> = [];

    const balloons: Array<AnatomyBalloon> = balloonTargets.flatMap((target) => {
        const anchor = getCalloutAnchor(target.partId, measurement);

        return anchor ? [
            {
                ...target,
                anchor: {
                    x: originX + anchor.x * scale,
                    y: originY + anchor.y * scale
                }
            }
        ] : [];
    }).sort((first, second) => first.number - second.number).map((target) => {
        const balloon = {
            x: target.anchor.x + BALLOON_OFFSET,
            y: target.anchor.y - BALLOON_OFFSET
        };

        while(placedBalloons.some((placed) => Math.abs(placed.x - balloon.x) < BALLOON_SIZE && Math.abs(placed.y - balloon.y) < BALLOON_SIZE)) {
            balloon.x += BALLOON_SIZE + 2;
        }

        placedBalloons.push(balloon);

        return {
            ...target,
            balloon
        };
    });

    return {
        stageWidth: Math.max(availableWidth, leftMargin + objectWidth + rightMargin),
        stageHeight: originY + height * scale + bottomMargin,
        gridStep: getGridStep(scale),
        balloons,
        originX,
        originY,
        chains,
        scale
    };
};

export const createAnatomyLayout = (options: AnatomyLayoutOptions): AnatomyLayout => {
    const rawChains = resolveChains(options.measurement, options.ghosts);

    const candidates = SCALES.filter((scale) => scale <= options.maxScale);

    for(const scale of candidates) {
        const layout = layoutForScale(options, rawChains, scale);

        if(layout.stageWidth <= options.availableWidth && (options.measurement.height * scale <= options.maxHeight || scale <= 1)) {
            return layout;
        }
    }

    return layoutForScale(options, rawChains, 1);
};

export const getScaleBarLength = (scale: number) => SCALE_BAR_LENGTHS.find((length) => length * scale >= MIN_SCALE_BAR_PIXELS) ?? SCALE_BAR_LENGTHS[SCALE_BAR_LENGTHS.length - 1] ?? 1;
